import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { FeaturedLayout } from '../layouts'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent } from '../ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import UsageLimitWarning from '../ui/UsageLimitWarning'
import { cn } from '../../lib/utils'
import { getOrganization } from '../../lib/supabase'
import { getBitacoraDeportes, getDeportesDeOrg } from '../../lib/services/deportesProceso'
import { conteosDeIds, resumenConteos } from '../../lib/deporteCambio'
import { ORG_TYPE_LABELS } from '../../constants/orgTypes'
import { useDeportes } from '../../hooks/useDeportes'
import DeporteCambioDialog from './DeporteCambioDialog'

const cuantos = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`

const formatFecha = (iso) =>
  new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })

const Estado = ({ d }) => (
  <span className="inline-flex flex-wrap items-center gap-x-1.5 text-sm">
    <span className={cn('h-2 w-2 rounded-full', d.habilitado ? 'bg-emerald-500' : 'bg-muted-foreground/40')} />
    {d.habilitado ? 'Habilitado' : 'No habilitado'}
    {d.habilitado && !d.catalogo_activo && (
      <span className="text-xs text-muted-foreground">· retirado del catálogo</span>
    )}
  </span>
)

const AccionBitacora = ({ b }) => {
  const deshab = b.accion === 'deshabilitar'
  return (
    <Badge
      variant="secondary"
      className={cn('font-medium', deshab
        ? 'bg-destructive/10 text-destructive hover:bg-destructive/10'
        : 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100')}
    >
      {deshab ? 'Deshabilitó' : 'Habilitó'}
    </Badge>
  )
}

const detalleBitacora = (b) => {
  const deshab = b.accion === 'deshabilitar'
  const lista = resumenConteos(conteosDeIds(deshab ? b.cerrado : b.restaurado))
  return lista.length ? `${deshab ? 'Cerró' : 'Reabrió'}: ${lista.join(', ')}` : '—'
}

// Deportes de UNA org, solo para el Platform Admin: tabla de administracion con lo que
// tiene cada deporte, el tope del plan y el historial. Cada cambio pasa por
// DeporteCambioDialog (bloqueos, motivo, nombre escrito, aviso al Owner).
const DeportesOrgPage = () => {
  const { orgId } = useParams()
  const navigate = useNavigate()
  const { label: deporteLabel } = useDeportes()
  const [org, setOrg] = useState(null)
  const [datos, setDatos] = useState(null) // { plan, tope, deportes }
  const [bitacora, setBitacora] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)
  const [cambio, setCambio] = useState(null) // { deporte: { id, nombre }, accion }

  const cargar = useCallback(async () => {
    setCargando(true)
    setError(null)
    const [o, dd, bit] = await Promise.all([
      getOrganization(orgId), getDeportesDeOrg(orgId), getBitacoraDeportes(orgId),
    ])
    if (o.error || dd.error || bit.error) {
      setError('No se pudieron cargar los deportes de la organización.')
    }
    setOrg(o.data || null)
    setDatos(dd.data || null)
    setBitacora(bit.data || [])
    setCargando(false)
  }, [orgId])

  useEffect(() => { cargar() }, [cargar])

  const tope = datos?.tope
  const alTope = !!tope && !tope.exento && tope.usados >= tope.maximo
  const deportes = datos?.deportes || []
  const abrir = (d, accion) => setCambio({ deporte: { id: d.deporte_id, nombre: d.nombre }, accion })

  return (
    <FeaturedLayout
      title={org ? `Deportes · ${org.name}` : 'Deportes'}
      subtitle={org
        ? `${ORG_TYPE_LABELS[org.type] || 'Organización'} · Plan ${datos?.plan?.nombre || 'sin asignar'}`
        : undefined}
    >
      <div className="space-y-6">
        <Button variant="ghost" className="px-0" onClick={() => navigate('/platform/organizations')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Organizaciones
        </Button>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {cargando ? (
          <p className="text-sm text-muted-foreground">Cargando deportes...</p>
        ) : (
          <>
            {tope && (
              <Card>
                <CardContent className="py-4">
                  <UsageLimitWarning
                    resource="Deportes del plan"
                    current={tope.usados}
                    max={tope.exento ? null : tope.maximo}
                  />
                  {alTope && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      El plan permite {tope.maximo} {tope.maximo === 1 ? 'deporte' : 'deportes'}: para habilitar otro, deshabilita uno o sube el plan.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Deporte</TableHead>
                      <TableHead className="hidden sm:table-cell">Estado</TableHead>
                      <TableHead className="hidden sm:table-cell text-right">Grupos</TableHead>
                      <TableHead className="hidden sm:table-cell text-right">Categorías</TableHead>
                      <TableHead className="hidden sm:table-cell text-right">Jugadores</TableHead>
                      <TableHead className="w-32"><span className="sr-only">Acción</span></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deportes.map((d) => (
                      <TableRow key={d.deporte_id} className={d.habilitado ? '' : 'text-muted-foreground'}>
                        <TableCell>
                          <span className="flex items-center gap-2">
                            {/* En el celular no cabe la columna Estado: queda el punto junto al nombre */}
                            <span
                              aria-hidden
                              className={cn('h-2 w-2 shrink-0 rounded-full sm:hidden', d.habilitado ? 'bg-emerald-500' : 'bg-muted-foreground/40')}
                            />
                            <span className={d.habilitado ? 'font-medium text-foreground' : ''}>{d.nombre}</span>
                          </span>
                          {/* Y las tres columnas de numeros bajan aqui (ux-rules §11) */}
                          {d.habilitado && (
                            <span className="block text-xs text-muted-foreground sm:hidden">
                              {[cuantos(d.grupos, 'grupo', 'grupos'), cuantos(d.categorias, 'categoría', 'categorías'),
                                cuantos(d.jugadores, 'jugador', 'jugadores')].join(' · ')}
                              {!d.catalogo_activo && ' · retirado del catálogo'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell"><Estado d={d} /></TableCell>
                        <TableCell className="hidden sm:table-cell text-right tabular-nums">{d.habilitado ? d.grupos : '—'}</TableCell>
                        <TableCell className="hidden sm:table-cell text-right tabular-nums">{d.habilitado ? d.categorias : '—'}</TableCell>
                        <TableCell className="hidden sm:table-cell text-right tabular-nums">{d.habilitado ? d.jugadores : '—'}</TableCell>
                        <TableCell className="text-right">
                          {/* Una sola accion por fila, a la vista. Deshabilitar es destructivo pero no
                              actua de un clic: abre el dialogo con bloqueos, motivo y nombre escrito
                              (mismo criterio que "Remover" en Jugadores). Las dos con borde, discretas. */}
                          {d.habilitado ? (
                            <Button size="sm" variant="outline" onClick={() => abrir(d, 'deshabilitar')}>
                              Deshabilitar
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" disabled={alTope} onClick={() => abrir(d, 'habilitar')}>
                              Habilitar
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <section className="space-y-2">
              <h2 className="text-lg font-semibold">Historial</h2>
              {bitacora.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Sin cambios registrados. El historial empieza con el primer cambio después del alta.
                </p>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    {/* Movil: lista (el motivo es texto libre y no cabe en una celda corta) */}
                    <div className="divide-y sm:hidden">
                      {bitacora.map((b) => (
                        <div key={b.id} className="space-y-1 px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <AccionBitacora b={b} />
                            <span className="font-medium">{deporteLabel(b.deporte_id)}</span>
                          </div>
                          <p className="text-muted-foreground">
                            {formatFecha(b.created_at)} · {b.autor?.nombre_completo || 'Administrador de la plataforma'}
                          </p>
                          <p>Motivo: {b.motivo}</p>
                          {detalleBitacora(b) !== '—' && (
                            <p className="text-muted-foreground">{detalleBitacora(b)}</p>
                          )}
                        </div>
                      ))}
                    </div>
                    {/* Escritorio: la tabla */}
                    <div className="hidden sm:block">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Fecha</TableHead>
                            <TableHead>Acción</TableHead>
                            <TableHead>Motivo</TableHead>
                            <TableHead>Por</TableHead>
                            <TableHead>Detalle</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {bitacora.map((b) => (
                            <TableRow key={b.id}>
                              <TableCell className="whitespace-nowrap">{formatFecha(b.created_at)}</TableCell>
                              <TableCell className="whitespace-nowrap">
                                <AccionBitacora b={b} /> <span className="ml-1">{deporteLabel(b.deporte_id)}</span>
                              </TableCell>
                              <TableCell className="text-muted-foreground">{b.motivo}</TableCell>
                              <TableCell className="whitespace-nowrap">{b.autor?.nombre_completo || 'Administrador de la plataforma'}</TableCell>
                              <TableCell className="text-muted-foreground">{detalleBitacora(b)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </section>
          </>
        )}
      </div>

      <DeporteCambioDialog
        open={!!cambio}
        onOpenChange={(v) => { if (!v) setCambio(null) }}
        org={org}
        deporte={cambio?.deporte}
        accion={cambio?.accion}
        onHecho={() => cargar()}
      />
    </FeaturedLayout>
  )
}

export default DeportesOrgPage
